import { initializeApp } from "firebase/app";
import {
  getMessaging,
  isSupported as isMessagingSupported,
} from "firebase/messaging";
import { getAnalytics, initializeAnalytics, isSupported } from "firebase/analytics";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

/** Web Push certificate. Belongs to the same project as the config above. */
export const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// .env is gitignored, so a variable nobody set is a silent undefined rather than
// a build error, and the failure surfaces much later as an unrelated Firebase
// error. Name the missing keys instead.
const missing = Object.entries({ ...firebaseConfig, vapidKey: VAPID_KEY })
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missing.length) {
  console.error(
    `[firebase] missing from .env: ${missing.join(", ")}. Restart the dev server after adding them.`
  );
}

const app = initializeApp(firebaseConfig);

// Cloud Messaging needs a SECURE CONTEXT - https, or http on localhost. Opening
// the dev server over a LAN address (http://10.x.x.x:3000) is not one, so the
// browser withholds ServiceWorker, PushManager and Notification, and calling
// getMessaging() there throws messaging/unsupported-browser at import time,
// taking the whole app down with it.
//
// So it sits behind isSupported(), the same way Analytics does below. That check
// is async, which is why callers await `messagingReady` rather than reading
// `messaging` directly. Where push is unavailable it resolves to null and the
// app carries on without notifications.
export let messaging = null;
export const messagingReady = isMessagingSupported()
  .then((supported) => {
    if (!supported) {
      console.warn(
        "[firebase] Push notifications are unavailable here, so none will arrive. " +
          "Cloud Messaging requires https or localhost - a LAN address such as " +
          `${window.location.origin} does not qualify. Everything else works normally.`
      );
      return null;
    }

    messaging = getMessaging(app);

    return messaging;
  })
  .catch((error) => {
    console.error("[firebase] Messaging failed to initialise", error);
    return null;
  });

// The worker lives in public/, which CRA copies verbatim — no webpack, no
// process.env — so it cannot read the config above. Passing it on the
// registration URL keeps this file the single source of truth: a page/worker
// project mismatch, which kills background notifications with no visible error,
// becomes impossible. measurementId is dropped because Analytics cannot run in a
// service worker.
const workerConfig = Object.fromEntries(
  ["apiKey", "authDomain", "projectId", "storageBucket", "messagingSenderId", "appId"]
    .map((key) => [key, firebaseConfig[key]])
);

export const swRegistration =
  "serviceWorker" in navigator
    ? navigator.serviceWorker
        .register(
          `/firebase-messaging-sw.js?${new URLSearchParams(workerConfig)}`
        )
        .catch((error) => {
          console.error("[firebase] service worker registration failed", error);
          return undefined;
        })
    : Promise.resolve(undefined);

/**
 * Anything that is not the /prod API prefix — localhost included — is treated as
 * debug traffic. Exported so src/analytics uses the same switch.
 */
export const IS_ANALYTICS_DEBUG = !(process.env.REACT_APP_PREFIX || "")
  .trim()
  .endsWith("/prod");

// getAnalytics() throws where measurement is unavailable (private windows, some
// embedded browsers), so it has to sit behind isSupported(). That check is async,
// which is why callers go through src/analytics rather than touching this export —
// the wrapper there buffers anything logged before this promise settles.
export let analytics = null;
export const analyticsReady = isSupported()
  .then((supported) => {
    if (!supported) {
      // Silent otherwise: every event would be dropped with no clue why. The
      // usual causes are blocked cookies, a tracking/ad blocker, or IndexedDB
      // being unavailable — all four checks isSupported() performs.
      console.warn(
        "[firebase] Analytics is not supported in this browser context, so no events will be sent. Check that cookies and IndexedDB are enabled and that no tracking blocker is active."
      );
      return null;
    }

    // debug_mode has to travel in the INITIAL gtag config call for GA4 to open a
    // DebugView session; setting it per event is not enough on web, which is why
    // localhost traffic never appeared there. initializeAnalytics must run before
    // any getAnalytics() call for the config to take effect.
    analytics = IS_ANALYTICS_DEBUG
      ? initializeAnalytics(app, { config: { debug_mode: true } })
      : getAnalytics(app);

    if (IS_ANALYTICS_DEBUG) {
      console.info(
        "[firebase] Analytics debug_mode is ON — events appear live in Firebase console → Analytics → DebugView."
      );
    }

    return analytics;
  })
  .catch((error) => {
    console.error("[firebase] Analytics failed to initialise", error);
    return null;
  });
